import {
  PB_PROTOCOL_VERSION,
  PB_SHELL_SOURCE,
  PB_CANVAS_SOURCE,
  ToShell,
  ToCanvas,
} from '~/protocol/pbProtocol';

export default defineNuxtPlugin(() => {
  if (!process.client) return;

  // Active in dev, or on the isolated builder instance (`npm run dev:builder` /
  // `ddev pb-server`) — never in normal production builds.
  if (!import.meta.dev && !useRuntimeConfig().public.pbBuilder) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get('pb-builder') !== '1') return;

  const parentOrigin = params.get('pb-origin') || '';

  const nuxtApp = useNuxtApp();

  // Per-block render revisions: bumping one forces a remount of just that block component
  // (re-running its onMounted DOM-library init). Read by globals/pageBuilder/index.vue keys
  // via the shared composable; stays an empty object outside the builder.
  const blockRevs = usePbBlockRevs();

  const ACTIVE_CLASS = 'pb-builder-active';
  const SELECTED_CLASS = 'pb-selected';

  const post = (message: Record<string, unknown>): void => {
    // Fail CLOSED: target the known parent (shell) origin, never '*'. These messages carry block ids
    // and inline-edited content; broadcasting them would expose that to any page framing this
    // preview. Without a parent origin (non-builder context) there's no shell to talk to — skip.
    if (!parentOrigin) return;
    try {
      window.parent?.postMessage(
        { source: PB_CANVAS_SOURCE, v: PB_PROTOCOL_VERSION, ...message },
        parentOrigin,
      );
    } catch {
      /* parent gone / blocked — ignore */
    }
  };

  const escapeId = (id: string): string =>
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(id)
      : id.replace(/["\\]/g, '\\$&');

  const blockOf = (target: EventTarget | null): Element | null =>
    target instanceof Element ? target.closest('[data-pb-id]') : null;

  // Comment anchors go beyond blocks: named page regions (data-pb-region — the article header
  // chrome, the TOC sidebar) accept comment pins too, but are comment-only — they never select,
  // never open the Inspector. Blocks win when nested (closest() picks the nearest ancestor).
  const commentAnchorOf = (target: EventTarget | null): Element | null =>
    target instanceof Element
      ? target.closest('[data-pb-id], [data-pb-region]')
      : null;

  const infoOf = (el: Element) => ({
    id: el.getAttribute('data-pb-id'),
    blockType: el.getAttribute('data-pb-type'),
    index: el.getAttribute('data-pb-index'),
  });

  // Recover the data-pb-* anchor for blocks whose component renders a FRAGMENT (multiple root
  // nodes). globals/pageBuilder/index.vue passes data-pb-id/-type/-index as fallthrough attributes
  // on the block <component>; Vue can only auto-apply those to a single-root component, so for a
  // multi-root block it silently drops them — leaving the block with no DOM anchor (un-selectable,
  // un-commentable). But the values still live on the block's component instance (`.attrs`), and in
  // dev/builder mode every rendered element exposes its owning instance via `__vueParentComponent`.
  // So we walk each .page-builder container's children and stamp the missing data-pb-* onto EVERY
  // root of such a block (e.g. the hero's <section> AND its trailing logo-garden <div>), so a click
  // anywhere in the block resolves to it. Block geometry is the UNION of those roots (unionRectFor),
  // keeping pin positions consistent. Idempotent: single-root blocks already carry the attribute.
  type BlockInstance = { attrs?: Record<string, unknown> };
  const ownerOf = (el: Element): BlockInstance | undefined =>
    (el as Element & { __vueParentComponent?: BlockInstance }).__vueParentComponent;

  const tagBlockAnchors = (): void => {
    document.querySelectorAll('.page-builder').forEach((container) => {
      Array.from(container.children).forEach((child) => {
        if (child.hasAttribute('data-pb-id')) return;
        const attrs = ownerOf(child)?.attrs;
        if (attrs == null) return;
        const id = attrs['data-pb-id'];
        if (id == null) return;
        child.setAttribute('data-pb-id', String(id));
        if (attrs['data-pb-type'] != null) {
          child.setAttribute('data-pb-type', String(attrs['data-pb-type']));
        }
        if (attrs['data-pb-index'] != null) {
          child.setAttribute('data-pb-index', String(attrs['data-pb-index']));
        }
      });
    });
  };

  // Coalesce re-tagging to one pass per frame (block mounts/remounts fire many mutations).
  let tagScheduled = false;
  const scheduleTagBlockAnchors = (): void => {
    if (tagScheduled) return;
    tagScheduled = true;
    requestAnimationFrame(() => {
      tagScheduled = false;
      tagBlockAnchors();
      renderBannerTools();
    });
  };

  let selectedEl: Element | null = null;
  let selectedId: string | null = null;

  // Floating toolbar on the selected block: reorder (↑ / ↓) and remove (✕). The reorder pair is
  // hidden for blocks in single-slot fields (the hero — the shell sends their ids as `fixed`;
  // there's nothing to swap them with). Nothing is applied locally — each action is posted to
  // the shell, which persists it to the draft and refreshes the canvas. It lives on <body> (not
  // inside the block), so the select-on-click capture handler ignores clicks on it.
  const toolbar = document.createElement('div');
  toolbar.className = 'pb-toolbar';
  toolbar.hidden = true;

  // Badge shown while inline-editing a CKEditor tag — names the tag (h2 / p / ul …). See positionTagBadge.
  const tagBadge = document.createElement('div');
  tagBadge.className = 'pb-tag-badge';
  tagBadge.hidden = true;

  let fixedBlockIds = new Set<string>();

  const toolbarButton = (
    label: string,
    title: string,
    onClick: () => void,
  ): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.title = title;
    btn.addEventListener('click', (event) => {
      // Keep the click from reaching the site's own handlers (links, nav plugins).
      event.preventDefault();
      event.stopImmediatePropagation();
      onClick();
    });
    toolbar.appendChild(btn);
    return btn;
  };

  const postForSelected = (message: Record<string, unknown>): void => {
    if (selectedEl) post({ ...infoOf(selectedEl), ...message });
  };

  const moveUpButton = toolbarButton('↑', 'Move up', () =>
    postForSelected({ type: ToShell.MOVE, direction: 'up' }),
  );
  const moveDownButton = toolbarButton('↓', 'Move down', () =>
    postForSelected({ type: ToShell.MOVE, direction: 'down' }),
  );
  toolbarButton('✕', 'Remove block', () => postForSelected({ type: ToShell.REMOVE }));

  const applyToolbarFixed = (): void => {
    const fixed = selectedId != null && fixedBlockIds.has(selectedId);
    moveUpButton.hidden = fixed;
    moveDownButton.hidden = fixed;
  };

  const positionToolbar = (): void => {
    if (toolbar.hidden || !selectedEl) return;
    const rect = selectedEl.getBoundingClientRect();
    const margin = 8;
    const height = toolbar.offsetHeight || 32;
    const width = toolbar.offsetWidth || 96;
    // Pin to the block's top-right corner, sticking to the viewport top while scrolled into a tall
    // block, but never escaping below the block itself.
    let top = Math.max(rect.top + margin, margin);
    top = Math.min(top, rect.bottom - height - margin);
    toolbar.style.top = `${top}px`;
    toolbar.style.left = `${Math.max(rect.right - width - margin, margin)}px`;
  };

  window.addEventListener('scroll', positionToolbar, {
    capture: true,
    passive: true,
  });
  window.addEventListener('resize', positionToolbar, { passive: true });

  // A block counts as "in view" if any meaningful part of it is within the viewport — used to avoid
  // yanking the canvas to a block the user can already see (e.g. one they just clicked, or re-clicked).
  const isInView = (el: Element): boolean => {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 && rect.top < vh;
  };

  const setSelected = (id: string | null, scroll = true): void => {
    selectedId = id;
    if (selectedEl) selectedEl.classList.remove(SELECTED_CLASS);
    selectedEl = id
      ? document.querySelector(`[data-pb-id="${escapeId(id)}"]`)
      : null;
    if (selectedEl) {
      selectedEl.classList.add(SELECTED_CLASS);
      // Only scroll when the block isn't already on screen: a click (and its echo back from the shell)
      // shouldn't move the canvas — the user is looking right at it — but a selection restored after an
      // iframe reload, or a block just added below the fold, should still be brought into view.
      if (scroll && !isInView(selectedEl)) {
        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    toolbar.hidden = !selectedEl;
    applyToolbarFixed();
    positionToolbar();
    // the block toolbar now claims a corner a banner's tools may have taken
    positionBannerTools();
  };

  // Capture phase + stopImmediatePropagation: a click selects the block instead of following
  // links or triggering the force-reload navigation plugin. In comment mode it pins a comment
  // composer to the clicked point instead. Clicks inside the builder's own UI (toolbar, pins,
  // popovers) are left to their own handlers.
  document.addEventListener(
    'click',
    (event) => {
      // Clicks inside the active inline text editor stay native (caret placement, drag-select).
      // A click anywhere else blurs the editor first (committing the edit), so by the time this
      // handler sees an outside click, `editing` is already null.
      if (
        editing &&
        event.target instanceof Node &&
        editing.el.contains(event.target)
      ) {
        // …but if the field being edited sits inside a link (e.g. a featureList card is an <a>),
        // block the link's navigation so a click while editing doesn't steer the canvas away. The
        // caret is set on mousedown, so preventDefault here only cancels the <a>'s activation.
        if (event.target instanceof Element && event.target.closest('a[href]')) {
          event.preventDefault();
        }
        return;
      }
      if (
        event.target instanceof Element &&
        (toolbar.contains(event.target) || commentLayer.contains(event.target) || event.target.closest('.pb-banner-tools'))
      ) {
        return;
      }
      if (commentMode) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const target = commentAnchorOf(event.target);
        if (target) {
          openComposer(target, event);
        } else {
          closeComposer();
        }
        return;
      }
      // Same-page anchors (the article TOC, in-body `#section` links) are the one link kind that's
      // safe to let through: they only scroll and rewrite location.hash, so the preview-token URL is
      // kept — unlike a real navigation. Leave the event fully native (no preventDefault, no
      // stopImmediatePropagation) so the site's own handler runs: the TOC's @click.prevent scroller,
      // or the smooth-anchors plugin for in-body links. Anything pointing at another document
      // (`/other#x`) is NOT this — it falls through to the guards below.
      const link =
        event.target instanceof Element
          ? event.target.closest('a[href]')
          : null;
      if (link instanceof HTMLAnchorElement && link.target !== '_blank') {
        let url: URL | null = null;
        try {
          url = new URL(link.href, window.location.href);
        } catch {
          url = null;
        }
        const samePath = (p: string) => p.replace(/\/+$/, '') || '/';
        const samePage =
          url !== null &&
          url.hash !== '' &&
          url.origin === window.location.origin &&
          samePath(url.pathname) === samePath(window.location.pathname);
        if (samePage) return;
      }
      const el = blockOf(event.target);
      if (!el) {
        // Outside any block (header, footer, …): links must not steer the canvas away from
        // the preview-token URL — but non-link UI (menu toggles, accordions) stays live.
        if (
          event.target instanceof Element &&
          event.target.closest('a[href]')
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return;
      }
      const info = infoOf(el);
      // Interactive controls INSIDE a block (accordion toggles, carousel arrows/dots, tabs) stay
      // live so the author can expand/scroll content right on the canvas — like it works on the
      // published site. We still select the block (so the Inspector follows), but let the native
      // click reach its handler instead of swallowing it. In-block links are NOT in this list, so
      // they still fall through to preventDefault below (they'd steer the canvas off the preview URL).
      const interactive =
        event.target instanceof Element
          ? event.target.closest(
              'button, [role="button"], [aria-expanded], summary,' +
                '.swiper-button-next, .swiper-button-prev, .swiper-pagination-bullet,' +
                '[data-pb-interactive]',
            )
          : null;
      if (interactive && el.contains(interactive)) {
        setSelected(info.id);
        post({ type: ToShell.SELECT, ...info });
        return; // no preventDefault / stopImmediatePropagation — the native toggle/scroll runs
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      setSelected(info.id);
      post({ type: ToShell.SELECT, ...info });
    },
    true,
  );

  let hovered: Element | null = null;
  document.addEventListener(
    'mouseover',
    (event) => {
      const el = blockOf(event.target);
      if (el === hovered) return;
      hovered = el;
      if (el) post({ type: ToShell.HOVER, ...infoOf(el) });
    },
    true,
  );

  // Banner tools live outside article HTML, so typing/saving can never persist these controls.
  const bannerTools = new Map<Element, HTMLElement>();
  const overlaps = (a: DOMRect, b: DOMRect): boolean =>
    a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  const positionBannerTools = (): void => {
    const toolbarRect = toolbar.hidden ? null : toolbar.getBoundingClientRect();
    for (const [banner, tools] of bannerTools) {
      const rect = banner.getBoundingClientRect();
      tools.hidden = commentMode || rect.top < 72 || rect.top > window.innerHeight - 30 || rect.width === 0;
      if (tools.hidden) continue;
      const margin = 8;
      const width = tools.offsetWidth || 72;
      const height = tools.offsetHeight || 30;
      const right = Math.min(rect.right, window.innerWidth) - width - margin;
      // The banner's top-right corner by default — but the block toolbar sits there too when the
      // banner opens its block, and a thin banner's button and copy fill that corner. So the tools
      // take the first spot that covers neither the toolbar nor the banner's buttons and text:
      // below the toolbar, the top-left corner, beside the button, or above the banner.
      const button = banner.querySelector('.bcta-btn')?.getBoundingClientRect();
      const corner = { left: right, top: rect.top + margin, inside: true };
      const spots: Array<{ left: number; top: number; inside: boolean }> = [
        corner,
        ...(toolbarRect ? [{ left: right, top: toolbarRect.bottom + 4, inside: true }] : []),
        { left: rect.left + margin, top: rect.top + margin, inside: true },
        ...(button ? [{ left: button.left - width - margin, top: button.top + (button.height - height) / 2, inside: true }] : []),
        { left: right, top: rect.top - height - 4, inside: false },
      ];
      // the copy counts by the extent of its text, not its block (a short eyebrow spans the column)
      const blocked = [
        ...(toolbarRect ? [toolbarRect] : []),
        ...Array.from(banner.querySelectorAll('.bcta-btn'), (el) => el.getBoundingClientRect()),
        ...Array.from(
          banner.querySelectorAll('.bcta__eyebrow, .bcta__h2, .bcta__h3, .bcta__lead, .bcta__body, .bcta__check-item'),
          (el) => {
            const range = document.createRange();
            range.selectNodeContents(el);
            return range.getBoundingClientRect();
          },
        ),
      ];
      const fits = (spot: { left: number; top: number; inside: boolean }): boolean => {
        const box = new DOMRect(spot.left, spot.top, width, height);
        if (spot.top < 72) return false;
        // an inside spot must lie within THIS banner: the spot below the block toolbar is the
        // toolbar's own row, which belongs to the banner at the block's top only
        if (spot.inside && (box.left < rect.left || box.top < rect.top || box.bottom > rect.bottom - margin)) return false;
        return !blocked.some((other) => overlaps(box, other));
      };
      const spot = spots.find(fits) ?? corner;
      tools.style.top = `${spot.top}px`;
      tools.style.left = `${Math.max(margin, spot.left)}px`;
    }
  };
  const renderBannerTools = (): void => {
    for (const [banner, tools] of bannerTools) {
      if (!banner.isConnected) { tools.remove(); bannerTools.delete(banner); }
    }
    document.querySelectorAll('.pb-blog-cta').forEach((banner) => {
      if (bannerTools.has(banner)) return;
      const bannerId = banner.className.match(/(?:^|\s)pb-blog-cta-([a-f0-9]{32})(?:\s|$)/)?.[1];
      const block = blockOf(banner);
      if (!bannerId || !block) return;
      const tools = document.createElement('div');
      tools.className = 'pb-banner-tools';
      tools.setAttribute('role', 'group');
      tools.setAttribute('aria-label', 'CTA banner controls');
      for (const [label, glyph, type] of [
        ['Edit CTA banner', '✎', ToShell.EDIT_BLOG_CTA],
        ['Delete CTA banner', '✕', ToShell.DELETE_BLOG_CTA],
      ] as const) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = glyph;
        button.title = label;
        button.setAttribute('aria-label', label);
        button.addEventListener('click', (event) => {
          event.preventDefault(); event.stopImmediatePropagation();
          post({ type, id: block.getAttribute('data-pb-id'), bannerId });
        });
        tools.appendChild(button);
      }
      bannerTools.set(banner, tools);
      document.body.appendChild(tools);
    });
    positionBannerTools();
  };
  window.addEventListener('scroll', positionBannerTools, { capture: true, passive: true });
  window.addEventListener('resize', positionBannerTools, { passive: true });
  window.addEventListener('load', positionBannerTools, { capture: true });

  // ---- Comments: viewport-scoped pins drawn over the page ---------------------------------------
  // The shell owns the comment data and Ajax (CSRF lives there); the bridge renders pins, thread
  // popovers and the composer in DOCUMENT coordinates (scroll- and iframe-scale-proof) and posts
  // the user's intents back ('comment:add' / 'comment:reply' / 'comment:resolve' /
  // 'comment:delete'). A pin's anchor is its block (data-pb-id) OR named region (data-pb-region)
  // + x/y as % of the anchor's rect + the viewport it was made on — only the active viewport's
  // unresolved pins are shown.
  type PbComment = {
    id: number;
    parentId: number | null;
    blockId: number | null;
    region?: string | null;
    viewport: string | null;
    x: number | null;
    y: number | null;
    body: string;
    mentions?: number[];
    author: string;
    authorId: number | null;
    date: string;
    resolved: boolean;
    jiraKey?: string | null;
    jiraUrl?: string | null;
    replies: PbComment[];
  };

  type MentionUser = { id: number; name: string; handle: string };

  const COMMENT_MODE_CLASS = 'pb-comment-mode';

  let commentItems: PbComment[] = [];
  let commentMe: { id?: number; admin?: boolean } = {};
  let mentionUsers: MentionUser[] = [];
  let commentViewport = 'desktop';
  let commentMode = false;
  let commentsVisible = true;
  // Whether thread popovers offer the "Ticket" button (set by the shell from its Jira config).
  let commentTicketEnabled = false;
  let openThreadId: number | null = null;
  let composerTarget: {
    blockId: string | null;
    region: string | null;
    x: number;
    y: number;
  } | null = null;

  const commentLayer = document.createElement('div');
  commentLayer.className = 'pb-comments';

  const threadPopover = document.createElement('div');
  threadPopover.className = 'pb-comment-popover';
  threadPopover.hidden = true;
  commentLayer.appendChild(threadPopover);

  const composerPopover = document.createElement('div');
  composerPopover.className = 'pb-comment-popover';
  composerPopover.hidden = true;
  commentLayer.appendChild(composerPopover);

  // The DOM selector for a comment anchor: a named region beats the block id (a thread carries
  // exactly one of the two).
  const anchorSelector = (
    blockId: number | string | null,
    region?: string | null,
  ): string | null =>
    region != null && region !== ''
      ? `[data-pb-region="${escapeId(region)}"]`
      : blockId != null
        ? `[data-pb-id="${escapeId(String(blockId))}"]`
        : null;

  type BlockRect = { left: number; top: number; width: number; height: number };

  // An anchor can render as multiple sibling roots (fragment components — see tagBlockAnchors —
  // or a region spanning several elements, like the article header's breadcrumbs + title + meta),
  // so its geometry is the UNION of every element carrying the anchor attribute. Using the union
  // keeps a comment's x/y % consistent whether the user clicked the first root or a later one,
  // since both storing (openComposer) and rendering (pins) measure against the same box.
  const unionRectFor = (
    blockId: number | string | null,
    region?: string | null,
  ): BlockRect | null => {
    const selector = anchorSelector(blockId, region);
    if (!selector) return null;
    const els = document.querySelectorAll(selector);
    if (!els.length) return null;
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      left = Math.min(left, r.left);
      top = Math.min(top, r.top);
      right = Math.max(right, r.right);
      bottom = Math.max(bottom, r.bottom);
    });
    return { left, top, width: right - left, height: bottom - top };
  };

  const pinPoint = (
    rect: BlockRect,
    x: number,
    y: number,
  ): { left: number; top: number } => ({
    left: rect.left + window.scrollX + (rect.width * x) / 100,
    top: rect.top + window.scrollY + (rect.height * y) / 100,
  });

  const placePopover = (
    popover: HTMLElement,
    left: number,
    top: number,
  ): void => {
    popover.hidden = false;
    const width = popover.offsetWidth || 300;
    const maxLeft =
      window.scrollX + document.documentElement.clientWidth - width - 12;
    popover.style.left = `${Math.max(Math.min(left + 14, maxLeft), window.scrollX + 12)}px`;
    popover.style.top = `${top + 14}px`;
  };

  const closeThread = (): void => {
    openThreadId = null;
    threadPopover.hidden = true;
  };

  const closeComposer = (): void => {
    composerTarget = null;
    composerPopover.hidden = true;
    composerPopover.innerHTML = '';
  };

  // Form layout: full-width textarea with its action buttons right-aligned underneath.
  const commentForm = (
    input: HTMLTextAreaElement,
    buttons: HTMLButtonElement[],
  ): HTMLDivElement => {
    const form = document.createElement('div');
    form.className = 'pb-comment-popover__form';
    form.appendChild(input);
    const actions = document.createElement('div');
    actions.className = 'pb-comment-popover__actions';
    buttons.forEach((button) => actions.appendChild(button));
    form.appendChild(actions);
    return form;
  };

  const commentField = (placeholder: string): HTMLTextAreaElement => {
    const textarea = document.createElement('textarea');
    textarea.className = 'pb-comment-popover__input';
    textarea.placeholder = placeholder;
    textarea.rows = 2;
    return textarea;
  };

  const commentButton = (
    label: string,
    onClick: () => void,
    primary = false,
  ): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className =
      'pb-comment-popover__btn' +
      (primary ? ' pb-comment-popover__btn--primary' : '');
    btn.textContent = label;
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      onClick();
    });
    return btn;
  };

  // @-mention autocomplete for a comment/reply textarea. Typing "@" opens a dropdown of CMS users;
  // picking one inserts "@Name " and records that user's id, which is sent with the comment so the
  // server can email the mentioned user. The returned menu must be appended to the textarea's form
  // (positioned relative) by the caller; getMentions() returns the ids whose token is still present.
  const attachMentions = (
    textarea: HTMLTextAreaElement,
  ): { menu: HTMLElement; getMentions: () => number[] } => {
    const chosen = new Map<number, string>(); // user id → the "@Name" token inserted
    const menu = document.createElement('div');
    menu.className = 'pb-mention-menu';
    menu.hidden = true;

    let matches: MentionUser[] = [];
    let active = 0;
    let tokenStart = -1; // index of the '@' being completed, or -1 when the menu is closed

    const hide = (): void => {
      menu.hidden = true;
      menu.innerHTML = '';
      tokenStart = -1;
    };

    // The "@query" (no spaces) immediately before the caret, or null.
    const queryAtCaret = (): { start: number; query: string } | null => {
      const caret = textarea.selectionStart ?? textarea.value.length;
      const match = textarea.value.slice(0, caret).match(/(^|\s)@([^\s@]*)$/);
      if (!match) return null;
      const query = match[2] ?? '';
      return { start: caret - query.length - 1, query };
    };

    const place = (): void => {
      menu.style.top = `${textarea.offsetTop + textarea.offsetHeight + 2}px`;
      menu.style.left = `${textarea.offsetLeft}px`;
      menu.style.width = `${textarea.offsetWidth}px`;
    };

    const pick = (user: MentionUser): void => {
      if (tokenStart < 0) return;
      const caret = textarea.selectionStart ?? textarea.value.length;
      const token = `@${user.handle}`;
      textarea.value =
        textarea.value.slice(0, tokenStart) +
        token +
        ' ' +
        textarea.value.slice(caret);
      const pos = tokenStart + token.length + 1;
      textarea.setSelectionRange(pos, pos);
      chosen.set(user.id, token);
      hide();
      textarea.focus();
    };

    const render = (): void => {
      menu.innerHTML = '';
      matches.forEach((user, i) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className =
          'pb-mention-menu__item' + (i === active ? ' is-active' : '');
        item.textContent = user.handle;
        item.title = user.name;
        // mousedown would blur the textarea before click; prevent it so focus stays put.
        item.addEventListener('mousedown', (event) => event.preventDefault());
        item.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          pick(user);
        });
        menu.appendChild(item);
      });
      menu.hidden = false;
      place();
    };

    const refresh = (): void => {
      const at = queryAtCaret();
      if (!at) {
        hide();
        return;
      }
      tokenStart = at.start;
      const query = at.query.toLowerCase();
      matches = mentionUsers
        .filter(
          (user) =>
            !query ||
            user.name.toLowerCase().includes(query) ||
            user.handle.toLowerCase().includes(query),
        )
        .slice(0, 8);
      active = 0;
      if (matches.length === 0) {
        hide();
        return;
      }
      render();
    };

    textarea.addEventListener('input', refresh);
    // Registered before the composer's own Escape handler so, while the menu is open, these keys
    // drive the menu and don't bubble to "close composer" / submit.
    textarea.addEventListener('keydown', (event) => {
      if (menu.hidden || matches.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        active = (active + 1) % matches.length;
        render();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        active = (active - 1 + matches.length) % matches.length;
        render();
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const choice = matches[active];
        if (choice) pick(choice);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        hide();
      }
    });
    // Close the menu when focus leaves — deferred so a click on an item still registers.
    textarea.addEventListener('blur', () => window.setTimeout(hide, 120));

    return {
      menu,
      getMentions: () =>
        [...chosen.entries()]
          .filter(([, token]) => textarea.value.includes(token))
          .map(([id]) => id),
    };
  };

  // A pinned thread that should be visible right now (unresolved, this viewport, block present).
  const visibleThread = (id: number | null): PbComment | null => {
    const thread = commentItems.find((t) => t.id === id);
    if (
      !thread ||
      thread.resolved ||
      thread.viewport !== commentViewport ||
      thread.x == null ||
      thread.y == null
    ) {
      return null;
    }
    const selector = anchorSelector(thread.blockId, thread.region);
    return selector && document.querySelector(selector) ? thread : null;
  };

  // Reposition-only (no DOM rebuild — keeps a reply being typed intact).
  const positionThread = (): void => {
    if (openThreadId == null) return;
    const thread = visibleThread(openThreadId);
    const rect = thread ? unionRectFor(thread.blockId, thread.region) : null;
    if (!thread || !rect) {
      closeThread();
      return;
    }
    const point = pinPoint(rect, thread.x as number, thread.y as number);
    placePopover(threadPopover, point.left, point.top);
  };

  const positionComposer = (): void => {
    if (!composerTarget || composerPopover.hidden) return;
    const rect = unionRectFor(composerTarget.blockId, composerTarget.region);
    if (!rect) {
      closeComposer();
      return;
    }
    const point = pinPoint(rect, composerTarget.x, composerTarget.y);
    placePopover(composerPopover, point.left, point.top);
  };

  // Render a comment body into an element, colouring "@Name" mentions blue. Names are matched against
  // known users (this comment's `mentions` ids first, then any mentionable user whose token appears),
  // so multi-word display names highlight correctly and stray "@text" stays plain.
  const escapeRe = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const renderCommentBody = (el: HTMLElement, comment: PbComment): void => {
    const body = comment.body;
    const names = new Set<string>();
    (comment.mentions || []).forEach((id) => {
      const user = mentionUsers.find((candidate) => candidate.id === id);
      if (user) {
        names.add(user.handle);
        names.add(user.name); // legacy mentions stored the display name
      }
    });
    mentionUsers.forEach((user) => {
      if (body.includes(`@${user.handle}`)) names.add(user.handle);
      if (body.includes(`@${user.name}`)) names.add(user.name);
    });

    if (names.size === 0) {
      el.textContent = body;
      return;
    }

    const pattern = new RegExp(
      '@(?:' +
        [...names]
          .sort((a, b) => b.length - a.length)
          .map(escapeRe)
          .join('|') +
        ')',
      'g',
    );
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(body)) !== null) {
      if (match.index > last) {
        el.appendChild(document.createTextNode(body.slice(last, match.index)));
      }
      const span = document.createElement('span');
      span.className = 'pb-mention';
      span.textContent = match[0];
      el.appendChild(span);
      last = match.index + match[0].length;
    }
    if (last < body.length) {
      el.appendChild(document.createTextNode(body.slice(last)));
    }
  };

  // Full rebuild of the open thread's popover (on open and when the data changes).
  const renderThread = (): void => {
    const thread = visibleThread(openThreadId);
    if (!thread) {
      closeThread();
      return;
    }

    threadPopover.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'pb-comment-popover__head';
    if (thread.jiraKey && thread.jiraUrl) {
      // Already turned into a ticket — link to it instead of offering the button again.
      const jira = document.createElement('a');
      jira.className = 'pb-comment-popover__btn pb-comment-popover__jira';
      jira.href = thread.jiraUrl;
      jira.target = '_blank';
      jira.rel = 'noopener';
      jira.textContent = thread.jiraKey;
      head.appendChild(jira);
    } else if (commentTicketEnabled) {
      head.appendChild(
        commentButton('Ticket', () =>
          post({ type: ToShell.COMMENT_TICKET, commentId: thread.id }),
        ),
      );
    }
    head.appendChild(
      commentButton('Resolve', () =>
        post({ type: ToShell.COMMENT_RESOLVE, commentId: thread.id, resolved: true }),
      ),
    );
    head.appendChild(commentButton('✕', closeThread));
    threadPopover.appendChild(head);

    [thread, ...thread.replies].forEach((comment) => {
      const row = document.createElement('div');
      row.className = 'pb-comment-popover__row';

      const meta = document.createElement('div');
      meta.className = 'pb-comment-popover__meta';
      const author = document.createElement('strong');
      author.textContent = comment.author;
      const date = document.createElement('span');
      date.textContent = comment.date;
      meta.appendChild(author);
      meta.appendChild(date);
      if (
        commentMe.admin ||
        (commentMe.id != null && comment.authorId === commentMe.id)
      ) {
        meta.appendChild(
          commentButton('Delete', () =>
            post({ type: ToShell.COMMENT_DELETE, commentId: comment.id }),
          ),
        );
      }

      const body = document.createElement('p');
      body.className = 'pb-comment-popover__body';
      renderCommentBody(body, comment);

      row.appendChild(meta);
      row.appendChild(body);
      threadPopover.appendChild(row);
    });

    const reply = commentField('Reply…');
    const replyMentions = attachMentions(reply);
    const send = commentButton(
      'Reply',
      () => {
        const text = reply.value.trim();
        if (!text) return;
        send.disabled = true;
        post({
          type: ToShell.COMMENT_REPLY,
          parentId: thread.id,
          body: text,
          mentions: replyMentions.getMentions(),
        });
      },
      true,
    );
    const replyForm = commentForm(reply, [send]);
    replyForm.appendChild(replyMentions.menu);
    threadPopover.appendChild(replyForm);

    positionThread();
  };

  const openComposer = (anchorEl: Element, event: MouseEvent): void => {
    closeThread();
    const blockId = anchorEl.getAttribute('data-pb-id');
    const region =
      blockId == null ? anchorEl.getAttribute('data-pb-region') : null;
    // Measure against the anchor's full extent (all roots), so a click in a later root — e.g. the
    // hero's logo garden, or the article header's meta bar — yields an x/y that the pin renderer
    // (also union-based) reproduces exactly.
    const rect = unionRectFor(blockId, region) || anchorEl.getBoundingClientRect();
    composerTarget = {
      blockId,
      region,
      x: Math.round(((event.clientX - rect.left) / rect.width) * 10000) / 100,
      y: Math.round(((event.clientY - rect.top) / rect.height) * 10000) / 100,
    };

    composerPopover.innerHTML = '';
    const input = commentField('Add a comment…');
    // Attach mentions BEFORE the Escape handler so an open menu swallows Escape (closes the menu,
    // not the composer).
    const mentions = attachMentions(input);
    input.addEventListener('keydown', (keyEvent) => {
      if (keyEvent.key === 'Escape') closeComposer();
    });
    const submit = commentButton(
      'Comment',
      () => {
        const text = input.value.trim();
        if (!text || !composerTarget) return;
        post({
          type: ToShell.COMMENT_ADD,
          blockId: composerTarget.blockId,
          region: composerTarget.region,
          x: composerTarget.x,
          y: composerTarget.y,
          body: text,
          mentions: mentions.getMentions(),
        });
        closeComposer();
      },
      true,
    );
    const cancel = commentButton('Cancel', closeComposer);
    const form = commentForm(input, [cancel, submit]);
    form.appendChild(mentions.menu);
    composerPopover.appendChild(form);

    // Unhide BEFORE positioning — positionComposer() skips hidden popovers (the resize path).
    composerPopover.hidden = false;
    positionComposer();
    input.focus();
  };

  const renderPins = (): void => {
    commentLayer
      .querySelectorAll('.pb-comment-pin')
      .forEach((pin) => pin.remove());

    // Pins hidden (shell's 👁 toggle): nothing renders and any open popover closes.
    if (!commentsVisible) {
      closeThread();
      closeComposer();
      return;
    }

    for (const thread of commentItems) {
      const visible = visibleThread(thread.id);
      if (!visible) continue;
      const rect = unionRectFor(thread.blockId, thread.region);
      if (!rect) continue;

      const point = pinPoint(rect, thread.x as number, thread.y as number);
      const pin = document.createElement('button');
      pin.type = 'button';
      pin.className = 'pb-comment-pin';
      pin.textContent = String(1 + thread.replies.length);
      pin.title = `${thread.author}: ${thread.body.slice(0, 80)}`;
      pin.style.left = `${point.left}px`;
      pin.style.top = `${point.top}px`;
      pin.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (openThreadId === thread.id) {
          closeThread();
        } else {
          closeComposer();
          openThreadId = thread.id;
          renderThread();
        }
      });
      commentLayer.appendChild(pin);
    }

    positionThread();
    positionComposer();
  };

  window.addEventListener('resize', renderPins, { passive: true });
  // Layout shifts (lazy images, fonts, block remounts) move the anchors — re-place the pins.
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => renderPins()).observe(document.body);
  }
  // Sticky anchors (the TOC sidebar region) move within the DOCUMENT while scrolling, so their
  // document-coordinate pins must follow. rAF-coalesced; for static anchors the recomputed
  // positions are identical, so this is visually a no-op.
  let pinScrollScheduled = false;
  window.addEventListener(
    'scroll',
    () => {
      if (pinScrollScheduled) return;
      pinScrollScheduled = true;
      requestAnimationFrame(() => {
        pinScrollScheduled = false;
        renderPins();
      });
    },
    { capture: true, passive: true },
  );

  // ---- Inline text editing: double-click a plain-text value on the canvas -----------------------
  // The clicked element is mapped back to a Craft field WITHOUT per-component annotations: the
  // page's GraphQL data already sits in the Nuxt payload (every block keyed by the same id as
  // data-pb-id), and plain-text fields render verbatim ({{ block.quote }}) — so an element whose
  // text equals exactly one of the block's string field values IS that field. No unique match
  // (transformed, duplicated or nested-repeater text) → no edit mode; the Inspector stays the
  // fallback — never a wrong-field save. The shell persists the edit (CSRF + draftId live there)
  // via the regular save-block action and answers with the usual per-block refresh, so the block
  // re-renders from whatever Craft actually stored.
  const FIELD_SKIP = new Set([
    'id',
    'uid',
    'slug',
    'uri',
    'url',
    'title',
    'typeHandle',
    'status',
    '__typename',
    'dateCreated',
    'dateUpdated',
    'postDate',
  ]);

  const normText = (value: string): string => value.replace(/\s+/g, ' ').trim();

  // Depth-first search of the payload for the block's GraphQL object: same id AND __typename
  // (data-pb-type) — nested entries (repeater items) carry ids too, the typename disambiguates.
  const findBlockData = (
    blockId: string,
    blockType: string | null,
  ): Record<string, unknown> | null => {
    const seen = new Set<object>();
    const stack: unknown[] = [nuxtApp.payload?.data];
    while (stack.length) {
      const node = stack.pop();
      if (!node || typeof node !== 'object' || seen.has(node)) continue;
      seen.add(node);
      if (Array.isArray(node)) {
        stack.push(...node);
        continue;
      }
      const obj = node as Record<string, unknown>;
      if (
        String(obj['id'] ?? '') === blockId &&
        (!blockType || obj['__typename'] === blockType)
      ) {
        return obj;
      }
      stack.push(...Object.values(obj));
    }
    return null;
  };

  // Plain-text projection of a CKEditor field's HTML, for value-matching the rendered element.
  const plainFromHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || '';
  };

  // Heading tags edit as a single line (Enter commits); other tags keep Enter for line breaks.
  const isHeadingTag = (el: Element): boolean => /^H[1-6]$/.test(el.tagName);

  // Markup an inline rich edit may keep — aligned with what Craft's CKEditor configs allow. Anything
  // else is unwrapped (its text kept); all attributes are dropped except href/target on <a>. This is
  // a courtesy clean for the author's view + change detection — Craft's HTML Purifier is the real
  // authority when the field saves.
  const RICH_TAGS = new Set([
    'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'A', 'UL', 'OL', 'LI',
    // ALL heading levels — a hero renders <h1>, sections <h2>/<h3>. Omitting H1/H5/H6 let
    // cleanHtmlString unwrap them to bare text, which CKEditor then re-wrapped as a <div> on save.
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE',
  ]);
  // Removed wholesale (tag AND contents) — leaving their text would be junk, not copy.
  const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED']);
  const cleanHtmlString = (html: string): string => {
    const root = document.createElement('div');
    root.innerHTML = html;
    const walk = (node: Element, isTop: boolean): void => {
      Array.from(node.children).forEach((child) => {
        // A saved raw HTML embed is an opaque sibling, not editable prose. Walking into it
        // unwraps its card containers and strips classes/images on an unrelated paragraph edit.
        // Craft still validates the complete field on save.
        if (child.classList.contains('raw-html-embed')) return;
        if (DROP_TAGS.has(child.tagName)) {
          child.remove();
          return;
        }
        walk(child, false);
        Array.from(child.attributes).forEach((attr) => {
          const keep = child.tagName === 'A' && (attr.name === 'href' || attr.name === 'target');
          if (!keep) child.removeAttribute(attr.name);
        });
        if (child.tagName === 'DIV' && isTop) {
          // A TOP-LEVEL <div> is a block line contenteditable produced (or a block it swapped in on a
          // select-all-then-type). Rename it to <p> so it stays a paragraph — never a stray <div> or
          // bare text that CKEditor would re-wrap in its own block on save.
          const p = document.createElement('p');
          while (child.firstChild) p.appendChild(child.firstChild);
          child.replaceWith(p);
        } else if (!RICH_TAGS.has(child.tagName)) {
          // Any other disallowed element — a NESTED div, span, font… — is just a wrapper: unwrap it,
          // keeping its (already-cleaned) contents, so nested divs don't become invalid nested <p>s.
          const parent = child.parentNode;
          if (parent) {
            while (child.firstChild) parent.insertBefore(child.firstChild, child);
            parent.removeChild(child);
          }
        }
      });
    };
    walk(root, true);
    return root.innerHTML.trim();
  };

  // Where an editable value lives, so the shell can save it back. A top-level block field saves through
  // the block; a nested value saves through its OWNER (the block for a matrix row directly on the block,
  // or a reusable section entry — addressed by its payload `id` — for the block's linked content), with
  // the matrix field key + row index locating the row. Reusable saves are copy-on-write on the backend.
  type InlineTarget =
    | { scope: 'block'; field: string }
    | { scope: 'block-matrix'; matrixKey: string; rowIndex: number; field: string }
    | { scope: 'reusable'; reusableId: string; field: string }
    | { scope: 'reusable-matrix'; reusableId: string; matrixKey: string; rowIndex: number; field: string };

  // A candidate editable value: its normalised text (for matching the clicked element), whether it's a
  // CKEditor rich field, and where to save it.
  type InlineCandidate = { norm: string; rich: boolean; target: InlineTarget };

  // The scalar / CKEditor fields directly on one payload object, each turned into a candidate via
  // makeTarget(fieldKey). Two kinds qualify: plain strings ({{ x }}) matched by exact text and edited as
  // text; CKEditor { html } objects (v-html) matched by their plain text and edited one tag at a time.
  // Metadata (FIELD_SKIP) and empty values never qualify.
  const collectScalars = (
    obj: Record<string, unknown>,
    makeTarget: (field: string) => InlineTarget,
    allowTitle = false,
  ): InlineCandidate[] => {
    const out: InlineCandidate[] = [];
    for (const [key, value] of Object.entries(obj)) {
      // `title` is skipped for a top-level BLOCK (often auto-generated, or it duplicates a heading
      // field — ambiguous). But a NESTED item's title (a featureList row, a card) is real editable
      // content, so allow it there. A genuine reusable section entry's title is the locked provenance
      // label; the backend rejects that write (the edit reverts), so offering it here stays harmless.
      if (key === 'title' ? !allowTitle : FIELD_SKIP.has(key)) continue;
      if (typeof value === 'string') {
        if (value.trim() !== '') out.push({ norm: normText(value), rich: false, target: makeTarget(key) });
        continue;
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const html = (value as Record<string, unknown>)['html'];
        if (typeof html === 'string' && html.trim() !== '') {
          const text = plainFromHtml(html);
          if (text.trim() !== '') out.push({ norm: normText(text), rich: true, target: makeTarget(key) });
        }
      }
    }
    return out;
  };

  const objectsIn = (value: unknown): Record<string, unknown>[] =>
    Array.isArray(value)
      ? (value.filter((x) => x != null && typeof x === 'object' && !Array.isArray(x)) as Record<string, unknown>[])
      : [];

  // Entry-identity fields that only a real, routable, section-owned entry carries (a blogPost, a
  // resource, a customer…). A nested Matrix row (featureList → list, a card) has none at the top
  // level — its own link lives under a `linkField`/`button` object, never a bare `url`/`uri`/`slug`.
  const ENTRY_IDENTITY = ['url', 'uri', 'slug'];

  // Is this id-bearing array a RELATION to entries from ANOTHER section (Resources → blogPosts,
  // related resources/customers/…)? Those are canonical content reused across many pages — editing
  // one inline would change it everywhere — so they are NOT inline-editable. We detect them by their
  // routable identity (a non-empty top-level url/uri/slug) and skip the whole array, so the canvas
  // never opens an editor for them. Nested Matrix rows, which carry no such identity, stay editable.
  const isRelatedEntries = (items: Record<string, unknown>[]): boolean =>
    items.some((it) => ENTRY_IDENTITY.some((k) => typeof it[k] === 'string' && (it[k] as string).length > 0));

  // All editable candidates for a block: its own fields, its matrix rows' fields, and — one relation
  // deep — its reusable section entries' own fields and their matrix rows' fields. An array of objects
  // is read as a RELATION (reusable, save via its `id`) when its items carry an `id`, else as a MATRIX
  // on the owner (save via owner + key + index). Deeper nesting isn't collected (it falls back to the
  // Inspector). The backend re-validates every target.
  const inlineFieldsOf = (data: Record<string, unknown>): InlineCandidate[] => {
    const out: InlineCandidate[] = [];
    out.push(...collectScalars(data, (field) => ({ scope: 'block', field })));

    for (const [key, value] of Object.entries(data)) {
      if (FIELD_SKIP.has(key)) continue;
      const items = objectsIn(value);
      if (!items.length) continue;
      // Entries related from another section (e.g. Resources → blogPosts) are shared canonical
      // content, not this page's own nested blocks — never inline-editable. Skip them entirely so
      // no editor opens; nested Matrix rows (featureList → list) carry no routable identity and fall
      // through to the editable paths below.
      if (isRelatedEntries(items)) continue;
      const isReusable = items.every((it) => it['id'] != null);

      items.forEach((item, index) => {
        if (isReusable) {
          const reusableId = String(item['id']);
          out.push(...collectScalars(item, (field) => ({ scope: 'reusable', reusableId, field }), true));
          for (const [k2, v2] of Object.entries(item)) {
            if (FIELD_SKIP.has(k2)) continue;
            objectsIn(v2).forEach((row, rowIndex) => {
              out.push(
                ...collectScalars(row, (field) => ({
                  scope: 'reusable-matrix',
                  reusableId,
                  matrixKey: k2,
                  rowIndex,
                  field,
                }), true),
              );
            });
          }
        } else {
          out.push(
            ...collectScalars(item, (field) => ({ scope: 'block-matrix', matrixKey: key, rowIndex: index, field }), true),
          );
        }
      });
    }
    return out;
  };

  let editing: {
    el: HTMLElement;              // the element made contenteditable (the tag, or the plain element)
    blockId: string;             // the owning top-level block (for the save message + canvas refresh)
    target: InlineTarget;        // where the value saves (top-level / matrix row / reusable / reusable row)
    rich: boolean;               // CKEditor field (commit the field's HTML) vs plain string
    root: HTMLElement | null;    // rich: the field's render root, serialised on commit
    outer: boolean;              // rich: serialise root.outerHTML (a bare tag) vs innerHTML (a wrapper)
    singleLine: boolean;         // Enter commits (headings + plain) vs inserts a line break
    original: string;            // rich: el.innerHTML (to revert the tag); plain: el.textContent
    originalValue: string | null; // rich: the field's cleaned HTML at start, for change detection
  } | null = null;

  // Zero-width space used as a Shift+Enter caret helper (see insertLineBreak); stripped on commit.
  const ZWSP = String.fromCharCode(0x200b);

  // A small badge naming the tag being edited (h2 / p / ul \u2026), pinned to the tag's top-left. Lives on
  // <body> like the toolbar, and is pointer-events:none so the select-on-click handler ignores it.
  const positionTagBadge = (): void => {
    if (tagBadge.hidden || !editing) return;
    const rect = editing.el.getBoundingClientRect();
    const height = tagBadge.offsetHeight || 18;
    tagBadge.style.top = `${Math.max(rect.top - height - 2, 2)}px`;
    tagBadge.style.left = `${Math.max(rect.left, 4)}px`;
  };
  window.addEventListener('scroll', positionTagBadge, { capture: true, passive: true });
  window.addEventListener('resize', positionTagBadge, { passive: true });

  // Post the committed value to the shell, routed by where it lives. A top-level block field uses the
  // existing INLINE_EDIT; a matrix row or reusable-entry value uses INLINE_EDIT_NESTED, carrying the
  // owner + matrix key + row index so the shell can save it copy-on-write.
  const emitEdit = (blockId: string, target: InlineTarget, value: string): void => {
    if (target.scope === 'block') {
      post({ type: ToShell.INLINE_EDIT, blockId, field: target.field, value });
      return;
    }
    post({
      type: ToShell.INLINE_EDIT_NESTED,
      blockId,
      field: target.field,
      value,
      reusableId: 'reusableId' in target ? target.reusableId : null,
      matrixKey: 'matrixKey' in target ? target.matrixKey : null,
      rowIndex: 'rowIndex' in target ? target.rowIndex : null,
    });
  };

  const finishEdit = (commit: boolean): void => {
    if (!editing) return;
    const { el, blockId, target, rich, root, outer, original, originalValue } = editing;
    editing = null;
    el.removeEventListener('keydown', onEditKeydown);
    el.removeEventListener('blur', onEditBlur);
    el.removeEventListener('paste', onEditPaste);
    el.removeAttribute('contenteditable');
    el.classList.remove('pb-inline-editing');
    tagBadge.hidden = true;

    if (rich && root) {
      // Serialise the WHOLE field (the edited tag plus its untouched siblings) and commit it. The
      // edited tag keeps its own element (we edited inside it), so its <hN>/<p> survives \u2014 no re-wrap
      // needed. Strip the Shift+Enter caret-helper zero-width spaces. Craft's purifier finalises the markup.
      const value = cleanHtmlString(outer ? root.outerHTML : root.innerHTML).split(ZWSP).join('');
      if (!commit || value === originalValue) {
        el.innerHTML = original; // cancelled or unchanged: restore the tag (siblings never changed)
        return;
      }
      emitEdit(blockId, target, value);
      return;
    }

    // Plain string field. Read BEFORE teardown: innerText is CSS-aware, and the editing class's
    // pre-wrap keeps typed breaks (Shift+Enter) from collapsing into spaces.
    const text = (el.innerText || '').replace(/\u00a0/g, ' ').trim();
    if (!commit || normText(text) === normText(original)) {
      el.textContent = original; // cancelled or unchanged: put back the exact stored text
      return;
    }
    emitEdit(blockId, target, text);
  };

  // Insert a <br> at the caret. Done manually so it works in a plaintext-only heading too, where the
  // browser would otherwise insert a bare "\n" that collapses to a space on render. The zero-width
  // space gives the caret a focusable spot after a trailing <br>; it's stripped from the value on commit.
  const insertLineBreak = (): void => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const br = document.createElement('br');
    range.insertNode(br);
    const filler = document.createTextNode(ZWSP);
    br.after(filler);
    range.setStart(filler, filler.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const onEditKeydown = (event: KeyboardEvent): void => {
    // The page's own hotkey handlers must not fire while typing.
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      finishEdit(false);
    } else if (event.key === 'Enter' && event.shiftKey && editing?.rich) {
      // Shift+Enter inserts a line break inside a rich field (including plaintext-only headings).
      event.preventDefault();
      insertLineBreak();
    } else if (event.key === 'Enter' && !event.shiftKey && editing?.singleLine) {
      // Single-line tags (headings + plain fields): Enter commits. A multi-line tag (paragraph, list)
      // keeps Enter for new blocks and commits on blur (clicking away).
      event.preventDefault();
      finishEdit(true);
    }
  };

  const onEditBlur = (): void => finishEdit(true);

  // Rich fields: paste as PLAIN text so copied styles/markup never enter the field. (Craft's CKEditor
  // purifier is the final authority on save; this keeps what the author sees clean as they type.)
  const onEditPaste = (event: ClipboardEvent): void => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const startEdit = (
    el: HTMLElement,
    blockId: string,
    target: InlineTarget,
    opts: { rich: boolean; root?: HTMLElement; outer?: boolean; singleLine: boolean },
  ): void => {
    const root = opts.root ?? null;
    const outer = opts.outer ?? false;
    editing = {
      el,
      blockId,
      target,
      rich: opts.rich,
      root,
      outer,
      singleLine: opts.singleLine,
      original: opts.rich ? el.innerHTML : el.textContent || '',
      originalValue: opts.rich && root ? cleanHtmlString(outer ? root.outerHTML : root.innerHTML) : null,
    };
    if (opts.rich) {
      // Headings edit as plaintext-only: it matches their single-line nature AND avoids Chrome's
      // contenteditable quirk where select-all-then-type replaces the <h2> block with a <div> (which
      // then saved AS a <div>). Body tags stay rich (contenteditable) so bold/links/lists keep working;
      // paste is forced to plain text there. Either way the tag badge shows which tag is being edited.
      if (isHeadingTag(el)) {
        el.setAttribute('contenteditable', 'plaintext-only');
        if (!el.isContentEditable) el.setAttribute('contenteditable', 'true');
      } else {
        el.setAttribute('contenteditable', 'true');
        el.addEventListener('paste', onEditPaste);
      }
      tagBadge.textContent = el.tagName.toLowerCase();
      tagBadge.hidden = false;
    } else {
      // plaintext-only keeps typing/paste from introducing markup; older engines fall back to true
      // (the commit reads innerText, so markup wouldn't reach the field either way).
      el.setAttribute('contenteditable', 'plaintext-only');
      if (!el.isContentEditable) el.setAttribute('contenteditable', 'true');
    }
    el.classList.add('pb-inline-editing');
    el.addEventListener('keydown', onEditKeydown);
    el.addEventListener('blur', onEditBlur);
    el.focus();
    // Select the whole value — clearer affordance than a caret, and a native dblclick word
    // selection may have been suppressed by the click-capture handler anyway.
    const range = document.createRange();
    range.selectNodeContents(el);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    if (opts.rich) positionTagBadge();
  };

  // Pick what to edit for a matched field. A plain string field edits the exact matched element (as
  // before). A CKEditor (rich) field is edited ONE TAG at a time: climb the matched element to the
  // field's render root (the outermost ancestor within the block whose text is still the whole field
  // value — the v-html wrapper, or a bare tag), then take the top-level child of that root holding the
  // double-clicked node — the <hN>/<p>/<ul> being edited. Siblings stay untouched; commit serialises
  // the whole field. A heading tag edits single-line; anything else allows line breaks.
  const beginInlineEdit = (
    matchedEl: HTMLElement,
    clickTarget: HTMLElement,
    blockEl: Element,
    blockId: string,
    candidate: InlineCandidate,
  ): void => {
    if (!candidate.rich) {
      startEdit(matchedEl, blockId, candidate.target, { rich: false, singleLine: true });
      return;
    }
    let root: HTMLElement = matchedEl;
    while (
      root.parentElement &&
      root.parentElement !== blockEl &&
      blockEl.contains(root.parentElement) &&
      normText(root.parentElement.textContent || '') === candidate.norm
    ) {
      root = root.parentElement;
    }
    // The single tag to edit = the top-level child of root that contains the double-clicked node.
    let tagEl: HTMLElement = clickTarget;
    while (tagEl.parentElement && tagEl.parentElement !== root && root.contains(tagEl.parentElement)) {
      tagEl = tagEl.parentElement;
    }
    let outer = false;
    if (tagEl === root || tagEl.parentElement !== root) {
      // Clicked the wrapper itself (or outside any child): edit its first tag, or — when the root has
      // no element children — the root itself as a bare tag (serialise its outerHTML to keep the tag).
      const firstChild = root.firstElementChild as HTMLElement | null;
      if (firstChild) {
        tagEl = firstChild;
      } else {
        tagEl = root;
        outer = true;
      }
    }
    startEdit(tagEl, blockId, candidate.target, { rich: true, root, outer, singleLine: isHeadingTag(tagEl) });
  };

  document.addEventListener(
    'dblclick',
    (event) => {
      if (editing || commentMode) return;
      if (!(event.target instanceof Element)) return;
      if (
        toolbar.contains(event.target) ||
        commentLayer.contains(event.target)
      ) {
        return;
      }
      if (event.target.closest('.raw-html-embed')) return;
      const blockEl = blockOf(event.target);
      if (!blockEl) return;
      const info = infoOf(blockEl);
      const blockId = info.id;
      if (!blockId) return;

      const data = findBlockData(blockId, info.blockType);
      if (!data) return;
      const fields = inlineFieldsOf(data);
      if (!fields.length) return;

      // Walk from the clicked element up to the block root, looking for the deepest element whose
      // whole text equals exactly one field value. Two fields sharing the value → ambiguous → bail.
      let el: Element | null = event.target;
      while (el) {
        const norm = normText(el.textContent || '');
        if (norm) {
          const matches = fields.filter((candidate) => candidate.norm === norm);
          const match = matches.length === 1 ? matches[0] : null;
          if (match) {
            event.preventDefault();
            event.stopImmediatePropagation();
            beginInlineEdit(el as HTMLElement, event.target as HTMLElement, blockEl, blockId, match);
            return;
          }
          if (matches.length > 1) return;
        }
        el = el === blockEl ? null : el.parentElement;
      }
    },
    true,
  );

  // Fallback when the in-place refresh fails: a real reload, on a unique URL so it can't serve
  // a cached (stale) document (an iframe location.reload() may reuse the cached response).
  const hardReload = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.set('_pb', String(Date.now()));
    window.location.replace(url.toString());
  };

  // In-place canvas update (Storyblok-style): re-run the page's GraphQL queries — the preview
  // token is still in the URL, so the draft comes back — and let Vue patch only what changed.
  // No iframe reload: scroll position, hydration and widget state all survive. When the shell
  // names the edited block, its revision is bumped so that one block remounts (re-running its
  // onMounted DOM-library init); structural changes (add/remove/move) refresh without remounts.
  let refreshing = false;
  let refreshQueued = false;

  const refreshInPlace = (blockId: string | null): void => {
    // A data refresh re-renders (or remounts) blocks — an in-progress inline edit can't survive
    // the DOM patch, so cancel it rather than risk committing into a replaced element.
    if (editing) finishEdit(false);
    if (refreshing) {
      refreshQueued = true;
      return;
    }
    refreshing = true;
    nuxtApp
      .runWithContext(() => refreshNuxtData())
      .then(() => {
        // Remount the edited block only AFTER the fresh data is in. Bumping its revision up front
        // (before refreshNuxtData resolved) remounts the block against the OLD payload — so for the
        // whole fetch the PREVIOUS content shows, reverting the just-made inline edit until the new
        // value lands. With the new data already applied, the remount (which only re-runs the block's
        // onMounted DOM-library init) renders the new content immediately; the optimistic edit shown
        // in the canvas stays put the entire time.
        if (blockId) {
          blockRevs.value = {
            ...blockRevs.value,
            [blockId]: (blockRevs.value[blockId] || 0) + 1,
          };
        }
        return nextTick();
      })
      .then(() => {
        refreshing = false;
        if (refreshQueued) {
          // Edits landed while we were fetching — fetch once more so none are lost.
          refreshQueued = false;
          refreshInPlace(null);
          return;
        }
        // Re-tag fragment-block anchors (remounted blocks lose the manually-set attribute), then
        // re-attach the selection to the (possibly remounted) block, without re-scrolling, and
        // re-place the comment pins (block rects may have moved/remounted).
        tagBlockAnchors();
        setSelected(selectedId, false);
        renderPins();
        post({ type: ToShell.REFRESHED });
      })
      .catch(() => {
        refreshing = false;
        refreshQueued = false;
        hardReload();
      });
  };

  window.addEventListener('message', (event: MessageEvent) => {
    // Fail CLOSED: only honor messages from the known parent (shell) origin. Empty parentOrigin
    // (non-builder context) → ignore everything; nothing legitimate posts to this bridge there.
    if (!parentOrigin || event.origin !== parentOrigin) return;
    const data = event.data as {
      source?: string;
      type?: string;
      v?: number;
      id?: string | null;
      blockId?: string | number | null;
    } | null;
    if (!data || data.source !== PB_SHELL_SOURCE) return;
    if (data.v != null && data.v !== PB_PROTOCOL_VERSION) {
      // The shell was deployed out of step with this iframe — surface it (see docs/protocol.md).
      console.warn(
        `Page Builder: shell protocol v${data.v} ≠ canvas v${PB_PROTOCOL_VERSION} — reload the builder.`,
      );
    }
    if (data.type === ToCanvas.SELECT) setSelected(data.id ?? null);
    if (data.type === ToCanvas.REFRESH) {
      refreshInPlace(data.blockId != null ? String(data.blockId) : null);
    }
    if (data.type === ToCanvas.COMMENTS) {
      const payload = event.data as {
        items?: PbComment[];
        me?: { id?: number; admin?: boolean };
        users?: MentionUser[];
        viewport?: string;
        mode?: boolean;
        visible?: boolean;
        fixed?: Array<string | number>;
        ticket?: boolean;
      };
      commentItems = payload.items || [];
      commentMe = payload.me || {};
      mentionUsers = payload.users || [];
      commentViewport = payload.viewport || 'desktop';
      commentMode = !!payload.mode;
      commentsVisible = payload.visible !== false;
      commentTicketEnabled = !!payload.ticket;
      fixedBlockIds = new Set((payload.fixed || []).map(String));
      applyToolbarFixed();
      document.documentElement.classList.toggle(
        COMMENT_MODE_CLASS,
        commentMode,
      );
      if (!commentMode) closeComposer();
      renderPins();
      positionBannerTools();
      if (openThreadId != null) renderThread();
    }
  });

  const style = document.createElement('style');
  style.textContent = `
    .pb-banner-tools { position:fixed; z-index:2147483646; display:flex; gap:2px; padding:3px; background:#fff; border:1px solid #c7c7f9; border-radius:7px; box-shadow:0 2px 8px #0002; }
    .pb-banner-tools[hidden] {display:none;}
    .pb-banner-tools button {width:30px;height:30px;border:0;border-radius:4px;background:#fff;color:#4f46e5;font:20px/1 system-ui;cursor:pointer;}
    .pb-banner-tools button:hover, .pb-banner-tools button:focus-visible {background:#eef2ff;}
    .pb-banner-tools button:last-child {color:#b42318;}

    .${ACTIVE_CLASS} [data-pb-id] { cursor: pointer; }
    .${ACTIVE_CLASS} [data-pb-id]:hover { outline: 1px dashed rgba(99, 102, 241, 0.7); outline-offset: -1px; }
    .${ACTIVE_CLASS} [data-pb-id].${SELECTED_CLASS} { outline: 2px solid rgb(99, 102, 241); outline-offset: -2px; }
    .pb-toolbar {
      position: fixed;
      z-index: 2147483647;
      display: flex;
      gap: 2px;
      padding: 3px;
      border-radius: 8px;
      background: rgb(99, 102, 241);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    }
    .pb-toolbar[hidden] { display: none; }
    .pb-toolbar button[hidden] { display: none; }
    .pb-toolbar button {
      appearance: none;
      border: 0;
      margin: 0;
      padding: 0;
      width: 26px;
      height: 26px;
      display: grid;
      place-items: center;
      border-radius: 6px;
      background: transparent;
      color: #fff;
      font-size: 14px;
      line-height: 1;
      cursor: pointer;
    }
    .pb-toolbar button:hover { background: rgba(255, 255, 255, 0.2); }
    .${COMMENT_MODE_CLASS} [data-pb-id],
    .${COMMENT_MODE_CLASS} [data-pb-id]:hover,
    .${COMMENT_MODE_CLASS} [data-pb-region],
    .${COMMENT_MODE_CLASS} [data-pb-region]:hover { cursor: crosshair; }
    .${ACTIVE_CLASS} .pb-inline-editing {
      outline: 2px solid rgb(16, 185, 129) !important;
      outline-offset: 2px;
      cursor: text !important;
      user-select: text !important;
      -webkit-user-select: text !important;
      white-space: pre-wrap;
    }
    .pb-tag-badge {
      position: fixed;
      z-index: 2147483647;
      padding: 1px 7px;
      border-radius: 5px 5px 5px 0;
      background: rgb(16, 185, 129);
      color: #fff;
      font: 600 11px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
      pointer-events: none;
      user-select: none;
    }
    .pb-tag-badge[hidden] { display: none; }
    .pb-comments {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 0;
      z-index: 2147483646;
    }
    .pb-comment-pin {
      position: absolute;
      z-index: 1;
      transform: translate(-50%, -100%);
      width: 28px;
      height: 28px;
      padding: 0;
      border: 2px solid #fff;
      border-radius: 14px 14px 14px 2px;
      background: rgb(99, 102, 241);
      color: #fff;
      font: 600 12px/1 system-ui, sans-serif;
      display: grid;
      place-items: center;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
    .pb-comment-popover {
      position: absolute;
      z-index: 3;
      width: 300px;
      background: #fff;
      color: #1f2937;
      border-radius: 10px;
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.28);
      padding: 10px 12px;
      font: 13px/1.45 system-ui, sans-serif;
      text-align: left;
    }
    .pb-comment-popover[hidden] { display: none; }
    .pb-comment-popover__head {
      display: flex;
      justify-content: flex-end;
      gap: 6px;
      margin-bottom: 4px;
    }
    .pb-comment-popover__row { padding: 6px 0; border-top: 1px solid #e5e7eb; }
    .pb-comment-popover__meta {
      display: flex;
      align-items: baseline;
      gap: 8px;
      font-size: 12px;
      color: #6b7280;
    }
    .pb-comment-popover__meta strong { color: #111827; font-size: 13px; }
    .pb-comment-popover__meta .pb-comment-popover__btn { margin-left: auto; }
    .pb-comment-popover__body { margin: 4px 0 0; white-space: pre-wrap; word-break: break-word; }
    .pb-comment-popover__body .pb-mention { color: #2563eb; font-weight: 600; }
    .pb-comment-popover__form { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; position: relative; }
    .pb-mention-menu {
      position: absolute;
      z-index: 5;
      max-height: 184px;
      overflow-y: auto;
      background: #fff;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
      padding: 4px;
    }
    .pb-mention-menu[hidden] { display: none; }
    .pb-mention-menu__item {
      display: block;
      width: 100%;
      text-align: left;
      border: 0;
      background: transparent;
      color: #111827;
      border-radius: 4px;
      padding: 5px 8px;
      font: 13px/1.3 system-ui, sans-serif;
      cursor: pointer;
    }
    .pb-mention-menu__item:hover,
    .pb-mention-menu__item.is-active { background: #eef2ff; }
    .pb-comment-popover__actions { display: flex; justify-content: flex-end; gap: 6px; }
    .pb-comment-popover__input {
      width: 100%;
      box-sizing: border-box;
      min-height: 56px;
      padding: 6px 8px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font: inherit;
      resize: vertical;
      background: #fff;
      color: inherit;
    }
    .pb-comment-popover__btn {
      border: 1px solid #d1d5db;
      background: #fff;
      color: #374151;
      border-radius: 6px;
      padding: 4px 10px;
      font: 12px/1.3 system-ui, sans-serif;
      cursor: pointer;
      white-space: nowrap;
    }
    .pb-comment-popover__btn:hover { background: #f3f4f6; }
    .pb-comment-popover__jira {
      display: inline-flex;
      align-items: center;
      text-decoration: none;
      color: rgb(99, 102, 241);
      border-color: rgba(99, 102, 241, 0.5);
      font-weight: 600;
    }
    .pb-comment-popover__btn--primary {
      background: rgb(99, 102, 241);
      border-color: rgb(99, 102, 241);
      color: #fff;
    }
    .pb-comment-popover__btn--primary:hover { background: rgb(79, 82, 221); }
  `;
  document.head.appendChild(style);
  document.body.appendChild(toolbar);
  document.body.appendChild(tagBadge);
  document.body.appendChild(commentLayer);
  document.documentElement.classList.add(ACTIVE_CLASS);

  // Anchor every block (incl. fragment-root blocks that can't receive the data-pb-id attr) now and
  // whenever blocks mount/remount — initial hydration, lazy-loaded blocks, and in-place refreshes.
  tagBlockAnchors();
  renderBannerTools();
  scheduleTagBlockAnchors();
  new MutationObserver(scheduleTagBlockAnchors).observe(document.body, {
    childList: true,
    subtree: true,
  });

  post({ type: ToShell.READY });
});
